/* eslint-disable jsx-a11y/no-autofocus */
import MDBox from "components/Basics/MDBox";
import lod_ from "lodash";
import React, { useState, useCallback } from "react";
import { getLabel } from "helpers/dictionary";
import { createDictionarySkeleton } from "helpers/form";
import MDButton from "components/Basics/MDButton";
import {
	Icon,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Checkbox
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

const cellSx = {
	fontSize: "0.6rem",
	lineHeight: "1.2",
	padding: "4px 2px",
	whiteSpace: "normal",
	wordWrap: "break-word",
	borderLeft: "1px solid rgba(224, 224, 224, 1)"
};

const headerCellSx = {
	fontSize: "0.6rem",
	fontWeight: "700",
	lineHeight: "1.2",
	whiteSpace: "normal",
	padding: "6px 4px",
	borderBottom: "2px solid rgba(200, 200, 200, 1)",
	backgroundColor: "#f5f5f5",
	color: "#344767",
	textTransform: "uppercase",
	letterSpacing: "0.5px",
	position: "sticky",
	top: 0,
	zIndex: 1
};

/**
 * Editable cell component — double-click to edit, blur/Enter to commit
 */
const EditableCell = ({ value, type, editable, onChange, profile }) => {
	const [editing, setEditing] = useState(false);
	const [editValue, setEditValue] = useState(value);

	const displayValue = React.useMemo(() => {
		switch (type) {
			case "date":
			case "datetime":
				return value ? new Date(value).toLocaleDateString(profile?.language || "fr-FR") : "";
			case "boolean":
				return String(Boolean(value));
			case "number":
				return value != null ? parseFloat(value) : "";
			default:
				return value ?? "";
		}
	}, [value, type, profile]);

	const handleCommit = useCallback(() => {
		setEditing(false);
		let committed = editValue;
		if (type === "number") committed = parseFloat(editValue) || 0;
		if (type === "boolean") committed = editValue === "true" || editValue === true;
		onChange(committed);
	}, [editValue, type, onChange]);

	if (!editable) {
		return <span>{displayValue}</span>;
	}

	if (!editing) {
		return (
			<span
				onDoubleClick={() => {
					setEditValue(value ?? "");
					setEditing(true);
				}}
				style={{ cursor: "pointer", width: "100%", minHeight: 16, display: "block" }}
			>
				{displayValue}
			</span>
		);
	}

	if (type === "string") {
		return (
			<textarea
				style={{ width: "100%", minHeight: 60, fontSize: "0.6rem" }}
				value={editValue ?? ""}
				onChange={e => setEditValue(e.target.value)}
				onBlur={handleCommit}
				onKeyDown={e => {
					if (e.key === "Escape") {
						setEditing(false);
						setEditValue(value ?? "");
					}
				}}
				autoFocus
			/>
		);
	}

	return (
		<input
			style={{ width: "100%", fontSize: "0.6rem", padding: "2px 4px" }}
			type={type === "number" ? "number" : "text"}
			value={editValue ?? ""}
			onChange={e => setEditValue(e.target.value)}
			onBlur={handleCommit}
			onKeyDown={e => {
				if (e.key === "Enter") handleCommit();
				if (e.key === "Escape") {
					setEditing(false);
					setEditValue(value ?? "");
				}
			}}
			autoFocus
		/>
	);
};

/**
 * Input for array of objects, displayed as a table with add/remove row functionality.
 * Uses a plain MUI Table to avoid DataGrid MIT's 100-row pageSize limit.
 */
const LittleArrayForm = ({ dictionary, path, values, handleChange, disabled }) => {
	const profile = useSelector(selectCurrentProfile);
	const [selectedIDs, setSelectedIDs] = useState(new Set());

	const columns = React.useMemo(() => {
		const cols = [];
		Object.entries(dictionary).forEach(([key, item]) => {
			if (item.display !== false) {
				cols.push({
					field: key,
					headerName: getLabel(item.label, "fr") || key,
					type: (() => {
						switch (item.type) {
							case "date":
							case "datetime":
								return "date";
							case "boolean":
								return "boolean";
							case "number":
								return "number";
							default:
								return "string";
						}
					})(),
					editable: disabled ? false : (item.actions?.edit ?? true)
				});
			}
		});
		return cols;
	}, [dictionary, disabled]);

	const rows = values || [];

	const handleCellChange = useCallback(
		(rowIndex, field, newValue) => {
			const newValues = lod_.cloneDeep(values || []);
			lod_.set(newValues, `[${rowIndex}].${field}`, newValue);
			handleChange(path, newValues);
		},
		[values, handleChange, path]
	);

	const handleDeleteSelected = () => {
		if (selectedIDs.size === 0) return;
		const newValues = (values || []).filter((_, i) => !selectedIDs.has(i));
		handleChange(path, newValues);
		setSelectedIDs(new Set());
	};

	const handleAddRow = () => {
		const newRow = createDictionarySkeleton(dictionary, {});
		const newValues = [...(values || []), newRow];
		handleChange(path, newValues);
	};

	const allSelected = rows.length > 0 && selectedIDs.size === rows.length;
	const someSelected = selectedIDs.size > 0 && !allSelected;

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIDs(new Set());
		} else {
			setSelectedIDs(new Set(rows.map((_, i) => i)));
		}
	};

	const toggleSelectRow = index => {
		setSelectedIDs(prev => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	};

	return (
		<MDBox>
			<TableContainer
				sx={{
					maxHeight: 600,
					overflow: "auto"
				}}
			>
				<Table size="small" stickyHeader>
					<TableRow>
						{!disabled && (
							<TableCell padding="checkbox" sx={headerCellSx}>
								<Checkbox
									size="small"
									indeterminate={someSelected}
									checked={allSelected}
									onChange={toggleSelectAll}
									sx={{ padding: "2px" }}
								/>
							</TableCell>
						)}
						{columns.map(col => (
							<TableCell key={col.field} sx={headerCellSx}>
								{col.headerName}
							</TableCell>
						))}
					</TableRow>
					<TableBody>
						{rows.map((row, rowIndex) => (
							<TableRow key={rowIndex} hover selected={selectedIDs.has(rowIndex)}>
								{!disabled && (
									<TableCell padding="checkbox" sx={cellSx}>
										<Checkbox
											size="small"
											checked={selectedIDs.has(rowIndex)}
											onChange={() => toggleSelectRow(rowIndex)}
											sx={{ padding: "2px" }}
										/>
									</TableCell>
								)}
								{columns.map(col => (
									<TableCell key={col.field} sx={cellSx}>
										<EditableCell
											value={row[col.field]}
											type={col.type}
											editable={col.editable}
											profile={profile}
											onChange={newValue => handleCellChange(rowIndex, col.field, newValue)}
										/>
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{!disabled && (
				<MDBox display="flex" alignItems="center" p={1}>
					<MDButton
						iconOnly
						sx={{ mr: 1 }}
						variant="gradient"
						color="dark"
						size="small"
						onClick={handleAddRow}
					>
						<Icon>add</Icon>
					</MDButton>
					<MDButton
						iconOnly
						disabled={selectedIDs.size === 0}
						variant="outlined"
						color="error"
						size="small"
						onClick={handleDeleteSelected}
					>
						<Icon>delete</Icon>
					</MDButton>
				</MDBox>
			)}
		</MDBox>
	);
};

export default LittleArrayForm;
